// ============================================================================
// COMPONENTE: GAMIFICATION PROFILE (V2.0 - XP ENGINE)
// ARQUIVO: src/components/GamificationProfile.jsx
// OBJETIVO: Calcular nível do aluno com base no histórico de acertos.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auth } from '../shared/api/firebase/config';
import { fetchAllExamResults } from '../services/GamificationProfileRepository';

const LEVELS = [
    { min: 0, label: "Estagiário", color: "text-gray-400", icon: "fa-baby" },
    { min: 200, label: "Bacharel", color: "text-blue-400", icon: "fa-user-graduate" },
    { min: 1000, label: "Advogado Júnior", color: "text-green-400", icon: "fa-balance-scale" },
    { min: 3000, label: "Advogado Sênior", color: "text-[#C5A059]", icon: "fa-gavel" },
    { min: 6000, label: "Lenda Jurídica", color: "text-purple-400", icon: "fa-crown" }
];

const GamificationProfile = () =>
 {
    const [xp, setXP] = useState(0);
    const [stats, setStats] = useState({ totalQuestions: 0, totalHits: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadXP = async () => {
            if (!user) return setLoading(false);
            try {
                // Busca todo o histórico de resultados para somar XP
                const rows = await fetchAllExamResults(user.uid);
                let hits = 0;
                let questions = 0;

                rows.forEach((data) => {
                    hits += (data.score || 0);
                    questions += (data.total || 0);
                });

                setStats({ totalQuestions: questions, totalHits: hits });
                setXP(hits * 10); // Regra: 10 XP por acerto
            } catch (e) {
                console.error("Erro XP:", e);
            } finally {
                setLoading(false);
            }
        };
        loadXP();
    }, []);

    // Determina o Nível Atual e o Próximo
    let currentLevel = LEVELS[0];
    let nextLevel = LEVELS[1];

    for (let i = 0; i < LEVELS.length; i++) {
        if (xp >= LEVELS[i].min) {
            currentLevel = LEVELS[i];
            nextLevel = LEVELS[i + 1] || { min: xp * 1.5, label: "Mestre Supremo" }; // Cap infinito
        }
    }

    // Calcula progresso da barra
    const progress = Math.min(100, Math.max(0, ((xp - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100));

    if (loading) return <div className="bg-[#1a1e30] rounded-xl p-6 h-40 animate-pulse border border-gray-700"></div>;

    if (!user) return (
        <div className="bg-[#1a1e30] rounded-xl p-6 border border-gray-700 text-white text-center h-full flex flex-col justify-center items-center">
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-3">
                <i className="fas fa-lock text-gray-400"></i>
            </div>
            <h3 className="font-bold text-sm">Nível Bloqueado</h3>
            <p className="text-xs text-gray-400 mt-1">Faça login para acumular XP.</p>
        </div>
    );

    return (
        <div className="bg-[#1a1e30] rounded-xl p-6 border border-gray-700 text-white relative overflow-hidden shadow-lg group h-full flex flex-col justify-between">
            {/* Background Effect */}
            <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform duration-700">
                <i className={`fas ${currentLevel.icon} text-8xl`}></i>
            </div>

            <div className="relative z-10">
                <div className="flex justify-between items-end mb-4">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Seu Nível</p>
                        <h3 className={`text-2xl font-serif font-bold ${currentLevel.color} flex items-center gap-2`}>
                            <i className={`fas ${currentLevel.icon} text-lg`}></i> {currentLevel.label}
                        </h3>
                    </div>
                    <div className="text-right">
                        <span className="text-3xl font-black text-white">{xp}</span>
                        <span className="text-xs text-gray-500 font-bold ml-1">XP</span>
                    </div>
                </div>

                {/* Barra de Progresso */}
                <div className="w-full bg-gray-800 h-3 rounded-full mb-2 border border-gray-700 overflow-hidden relative">
                    <div 
                        className="bg-gradient-to-r from-[#C5A059] to-yellow-300 h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(197,160,89,0.5)]" 
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                
                <div className="flex justify-between text-[9px] text-gray-500 font-mono font-bold uppercase mb-4">
                    <span>{currentLevel.min} XP</span>
                    <span>Próximo: {nextLevel.label} ({nextLevel.min} XP)</span>
                </div>

                {/* Mini Stats Grid */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
                    <div className="bg-white/5 p-2 rounded border border-white/5 text-center">
                        <p className="text-[9px] text-gray-400 uppercase mb-1">Questões</p>
                        <p className="text-sm font-bold text-white">{stats.totalQuestions}</p>
                    </div>
                    <div className="bg-white/5 p-2 rounded border border-white/5 text-center">
                        <p className="text-[9px] text-gray-400 uppercase mb-1">Acertos</p>
                        <p className="text-sm font-bold text-green-400">{stats.totalHits}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GamificationProfile;